import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { columnsPropTypes, rowsPropTypes } from "../Table/tablePropsTypes"
import { CardList } from "../Cards"
import DraggableTable from "../Table/DraggableTable"
import BaseTable from "../Table/BaseTable"
import { DropdownMenu, Paginate, SearchInput, SectionHeading, StatusMessage } from "../."
import { MobileViewSelector } from "."
import { Box, StackView } from "@planningcenter/tapestry-react"
import { Menu, MenuButton, MenuList, MenuLink } from "@reach/menu-button"
import sorter from "utils/sorter"
import { css } from "@emotion/core"
import classNames from "classnames"
import { reOrder } from "utils/arrays"

/**
 * Adds default configuration properties to the consumer supplied columns.
 */
const columnWithDefaults = columns =>
  columns.map(c => ({
    ...c,
    text: c.label,
    isSortable: c.isSortable ?? false,
    isSelectable: c.isSelectable ?? false,
    isSelected: c.isSelected ?? true
  }))

/**
 * Holds a timeout for an active drag n drop interaction.
 */
let dragTimeout

/**
 * By default renders a Table on desktop browsers, a CardList on mobile browsers,
 * and allows users to select between the two.
 */
const DynamicDataView = ({
  className,
  columns,
  defaultSort = null,
  defaultDir = "asc",
  draggableType,
  emptyTableMessage,
  emptyTableImageUrl,
  footerField,
  hasTableHeader,
  heading,
  headingChildren,
  isDeletable,
  isLoading,
  onColumnSelector,
  onDeleteClick,
  onDragEnd,
  onPaginateClick,
  onRowClick,
  onCardClick,
  onScopeChange = null,
  onSearchChange = null,
  onSelectChange,
  onSortClick = null,
  overflowWrapperStyle = {},
  pagination,
  primaryField,
  rows: rowsFromProps = [],
  searchInputStyle = {},
  searchPlacement = "aboveTable",
  searchPlaceholder = "Search by name",
  searchInitValue = "",
  secondaryFields,
  selectableField,
  scopeByFilters,
  showStatusMessage = true,
  status = null,
  style,
  cardStyles,
  cardListStyles
}) => {
  /*************************************************
   * STATE & HOOKS
   *************************************************/

  // Mobile view state - intialized to user client.
  const [mobileViewSelected, setMobileViewSelected] = useState(window.isMobile)
  // Data items to populate the view.
  const [items, setItems] = useState([])
  // Selectable columns (Table only)
  const [selectedColumns, setSelectedColumns] = useState(columnWithDefaults(columns))
  const hasSelectableColumns = selectedColumns.some(c => c.isSelectable)
  // A status message to the user.
  const [message, setMessage] = useState(status)
  // The user's search term.
  const [searchValue, setSearchValue] = useState(searchInitValue)
  // User selected sort field and direction.
  const [sortBy, setSortBy] = useState(defaultSort)
  const [sortDir, setSortDir] = useState(defaultDir)
  // Filter scope.
  const [filterScope, setFilterScope] = useState(
    scopeByFilters
      ? scopeByFilters.reduce(
          (hash, filter) => ({
            ...hash,
            [filter.name]: filter.initValue ?? filter.options[0].value
          }),
          {}
        )
      : null
  )

  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    } else {
      handleScopeChange(filterScope)
    }
  }, [filterScope])

  // Set message and items on render
  useEffect(() => {
    setSelectedColumns(columnWithDefaults(columns))
    setMessage(status)
    setItems(sortBy ? sorter(sortBy, rowsFromProps, sortDir) : rowsFromProps)
  }, [columns, rowsFromProps, status])

  /*************************************************
   * HANDLERS
   *************************************************/

  /**
   * Handles an update to provided filters.
   */
  const handleScopeChange = scope => onScopeChange({ sortBy, sortDir, searchValue, scope })

  /**
   * Pagination handler.
   */
  const handlePaginateClick = params =>
    onPaginateClick({
      sortBy,
      sortDir,
      searchValue,
      ...params,
      scope: filterScope
    })
  // Reset Pagination if filtered response reduces page counts
  const modifiedPagination = {
    ...pagination,
    ...(pagination?.currentPage > pagination?.totalPages && {
      currentPage: 1,
      firstPage: true,
      lastPage: false,
      nextPage: 2,
      prevPage: null
    })
  }

  /**
   * Sort handler.
   */
  const handleSort = async ({ sortBy: column }) => {
    const dir = sortBy === column.cell && sortDir === "asc" ? "desc" : "asc"

    setSortBy(column.cell)
    setSortDir(dir)

    onSortClick
      ? await onSortClick({
          page: 1,
          sortBy: column.cell,
          sortDir: dir,
          searchValue,
          scope: filterScope
        })
      : setItems(sorter(column.cell, items, dir))
  }

  /**
   * Search handler
   */
  const handleSearchChange = async ({ searchValue: value }) => {
    setSearchValue(value)

    const filteredItems = await onSearchChange({
      items: rowsFromProps,
      searchValue: value,
      sortBy,
      sortDir,
      scope: filterScope
    })

    setItems(sortBy ? sorter(sortBy, filteredItems, sortDir) : filteredItems)
  }
  const handleSearchClear = async () => handleSearchChange({ searchValue: "" })

  /**
   * Handle drag n drop
   */
  const handleDragEnd = result => {
    const { source, destination } = result

    if (!destination || destination.index === source.index) {
      return
    }

    const newItems = reOrder(items, source.index, destination.index)

    setItems(newItems)
    setMessage({
      text: "Saving...",
      icon: "spinner"
    })

    if (dragTimeout) {
      clearTimeout(dragTimeout)
    }

    // Try and limit the request on dragging
    dragTimeout = setTimeout(
      () =>
        onDragEnd(newItems)
          .then(_r => {
            setMessage({
              text: "Saved!",
              icon: "general#check",
              type: "success"
            })
            setTimeout(() => setMessage(null), 2000)
          })
          .catch(_r =>
            setMessage({
              text: "Changes not saved",
              icon: "general#exclamation-triangle",
              type: "error"
            })
          ),
      1500
    )
  }

  /**
   * Handler for hiding and showing columns based on user selection. (Pretty sure.)
   */
  const handleSelectableClick = column => {
    const newColumns = selectedColumns.map(sc =>
      sc.cell === column.key ? { ...sc, isSelected: !column.isSelected } : sc
    )

    setSelectedColumns(newColumns)
    onColumnSelector && onColumnSelector(newColumns, column)
  }

  /*************************************************
   * CHILD PROPS
   *************************************************/

  const tableProps = {
    columns: hasSelectableColumns ? selectedColumns.filter(c => c.isSelected) : selectedColumns,
    rows: items,
    isLoading,
    className: className || "table",
    style,
    hasTableHeader,
    emptyTableMessage,
    emptyTableImageUrl,
    onRowClick,
    onSortClick: handleSort,
    sortBy,
    sortDir,
    isSelectable: !!onSelectChange,
    selectableField,
    onSelectChange,
    isDeletable,
    onDeleteClick,
    searchValue,
    onSearchChange: onSearchChange && handleSearchChange,
    onSearchClear: onSearchChange && handleSearchClear,
    searchPlacement,
    overflowWrapperStyle,
    searchInputStyle
  }

  const cardListProps = {
    items,
    primaryField,
    secondaryFields,
    footerField,
    // onCardClick can be set explicitly to null to bypass a card click action.
    onCardClick: onCardClick === undefined ? onRowClick : onCardClick,
    emptyListMessage: emptyTableMessage,
    emptyListImageUrl: emptyTableImageUrl,
    isLoading,
    isDeletable,
    onDeleteClick,
    isSelectable: !!onSelectChange,
    onSelectChange,
    selectableField,
    cardStyles,
    cardListStyles
  }

  /*************************************************
   * RENDERERS
   *************************************************/

  /**
   * Render the status messge.
   */
  const renderStatusMessage = () =>
    showStatusMessage && message && <StatusMessage style={{ marginRight: "1rem" }} {...message} />

  // Adds the mobile view selector to the consumer supplied headingChildren
  const renderHeadingChildren = () => {
    const showSelector = primaryField
    return (
      <div className="d-f jc-sb">
        {showSelector && (
          <MobileViewSelector
            className={classNames({ "mr-1": !!headingChildren })}
            mobileViewSelected={mobileViewSelected}
            setMobileViewSelected={setMobileViewSelected}
          />
        )}
        {headingChildren}
      </div>
    )
  }

  /**
   * Render column selector dropdown
   */
  const renderSelectableDropdown = () => {
    const selectableColumns = selectedColumns
      .filter(c => c.isSelectable)
      .map(c => ({
        key: c.cell,
        text: c.text,
        isSelected: c.isSelected,
        onClick: handleSelectableClick,
        css: css`
          & .checkbox-label {
            top: 0;
          }
        `
      }))

    return (
      <DropdownMenu
        text="View options"
        items={selectableColumns}
        className="action-btn btn2 ml-1"
      />
    )
  }

  /**
   * Renders the header which includes filters and searching given certain props.
   */
  const renderHeader = () => {
    return (
      <div
        className={classNames("dynamic-data-view__header-container", {
          mobile: mobileViewSelected
        })}
      >
        {onSearchChange && searchPlacement == "aboveHeader" && (
          <div className="dynamic-data-view__header-top">
            <StackView mediaQueries={{ sm: { axis: "horizontal", alignment: "center" } }}>
              {onScopeChange && scopeByFilters && (
                <StackView flex={1} padding={1} spacing={1} axis="horizontal">
                  {scopeByFilters.map(filter => (
                    <Menu key={filter.name}>
                      <MenuButton className="select--small select ws-nw w-a">
                        {filter.options.find(f => f.value == filterScope[filter.name]).label}
                      </MenuButton>
                      <MenuList>
                        {filter.options.map(option => (
                          <MenuLink
                            className="dropdown-menu-item w-100%"
                            key={option.value}
                            onClick={() => {
                              setFilterScope({ ...filterScope, [filter.name]: option.value })
                            }}
                          >
                            {option.label}
                          </MenuLink>
                        ))}
                      </MenuList>
                    </Menu>
                  ))}
                </StackView>
              )}
              <Box
                className={classNames({
                  "dynamic-data-view__search-input": onScopeChange && scopeByFilters
                })}
              >
                <SearchInput
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={e => handleSearchChange({ searchValue: e.target.value })}
                  onClear={handleSearchClear}
                  style={searchInputStyle}
                />
              </Box>
            </StackView>
          </div>
        )}

        {(heading.headerText || headingChildren) && (
          <SectionHeading className="dynamic-data-view__header" {...heading}>
            {renderStatusMessage()}
            {renderHeadingChildren()}
            {hasSelectableColumns && renderSelectableDropdown()}
          </SectionHeading>
        )}

        {!heading.headerText && !headingChildren && (
          <div className="ta-r">
            {renderStatusMessage()}
            {hasSelectableColumns && renderSelectableDropdown()}
          </div>
        )}
      </div>
    )
  }

  /**
   * Renders a draggable table or a Base table depending on the provided props.
   */
  const renderTable = () => {
    return (
      <>
        {onDragEnd ? (
          <DraggableTable {...tableProps} onDragEnd={handleDragEnd} draggableType={draggableType} />
        ) : (
          <BaseTable {...tableProps} />
        )}
      </>
    )
  }

  /**
   * Renders the pagination section if a pagination handler has been passed.
   */
  const renderPagination = () => {
    return (
      <>
        {onPaginateClick && !isLoading && pagination.totalPages > 1 && (
          <div className={classNames("mb-1", "pagination-wrapper", { mobile: mobileViewSelected })}>
            <Paginate
              pagination={modifiedPagination}
              isMobile={window.isMobile}
              onNavClick={handlePaginateClick}
            />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {renderHeader()}

      {mobileViewSelected ? (
        <CardList {...cardListProps} headingChildren={renderHeadingChildren()} />
      ) : (
        <>{renderTable()}</>
      )}

      {renderPagination()}
    </>
  )
}

DynamicDataView.propTypes = {
  heading: PropTypes.shape({
    headerText: PropTypes.node,
    headerSubText: PropTypes.node,
    headerActions: PropTypes.arrayOf(
      PropTypes.shape({
        onClick: PropTypes.func,
        linkTo: PropTypes.string,
        text: PropTypes.string
      })
    ),
    className: PropTypes.string,
    style: PropTypes.shape({})
  }),
  headingChildren: PropTypes.node,
  children: PropTypes.node,
  columns: columnsPropTypes,
  rows: rowsPropTypes,
  onDragEnd: PropTypes.func,
  onRowClick: PropTypes.func,
  onCardClick: PropTypes.func,
  onColumnSelector: PropTypes.func,
  draggableType: PropTypes.string,
  status: PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.string,
    type: PropTypes.string
  }),
  primaryField: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
  secondaryFields: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node])
  ),
  footerField: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
  className: PropTypes.string,
  style: PropTypes.object,
  emptyTableMessage: PropTypes.string,
  emptyTableImageUrl: PropTypes.string,
  onSortClick: PropTypes.func,
  defaultSort: PropTypes.string,
  defaultDir: PropTypes.string,
  isSelectable: PropTypes.bool,
  onSelectChange: PropTypes.func,
  selectableField: PropTypes.string,
  isLoading: PropTypes.bool,
  hasTableHeader: PropTypes.bool,
  onSearchChange: PropTypes.func,
  showStatusMessage: PropTypes.bool,
  isDeletable: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  searchPlacement: PropTypes.oneOf(["aboveHeader", "aboveTable"]),
  onPaginateClick: PropTypes.func,
  pagination: PropTypes.shape({
    nextPage: PropTypes.number,
    prevPage: PropTypes.number,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number
  }),
  overflowWrapperStyle: PropTypes.object,
  searchInputStyle: PropTypes.object,
  searchPlaceholder: PropTypes.string,
  searchInitValue: PropTypes.string,
  scopeByFilters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      initValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
        })
      ).isRequired
    })
  ),
  onScopeChange: PropTypes.func,
  cardStyles: PropTypes.object,
  cardListStyles: PropTypes.object
}

export default DynamicDataView
